import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useSettingActions } from 'src/store/settings/actions';
import { RootState } from 'src/store/reducers/state';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import moment from 'moment';
import Loader from 'src/components/loader';

const UpdateOpenTextModal = () => {
  const dispatch = useDispatch();
  const settingAction = useSettingActions(dispatch);
  const ref = useRef<HTMLButtonElement>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [actionTimeError, setActionTimeError] = useState<boolean>(false);
  const [displayResultData, setDisplayResultData] = useState(false);
  const [time, setTime] = useState<any>('00:00:00');
  const [isDataSubmitted, setIsDataSubmitted] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm();

  const { singlePollTemplate, isTemplateUpdated, isTemplateUpdatedLoading } = useSelector(
    (state: RootState) => {
      return {
        singlePollTemplate: state.setting.singlePollTemplate,
        isTemplateUpdated: state.setting.isTemplateUpdated,
        isTemplateUpdatedLoading: state.setting.isTemplateUpdatedLoading
      };
    }
  );

  const onChange = (e: any) => {
    setTime(moment(e).format('HH:mm:ss'));
  };

  const onSubmit = (data: any) => {
    setIsDataSubmitted(true);
    const payloadData: any = {
      templateId: singlePollTemplate?.templateId,
      pollTitle: data.pollTitle,
      templateName: singlePollTemplate.templateName,
      pollType: 2,
      quizzes: {
        id: singlePollTemplate?.quizzes?.id,
        name: data.pollTitle,
        questions: [
          {
            id: singlePollTemplate?.quizzes?.questions[0]?.id,
            position: 1,
            questionText: data.pollTitle,
            description: data.description
          }
        ]
      }
    };
    if (singlePollTemplate) {
      payloadData.templateId = singlePollTemplate.templateId;
      payloadData.quizzes.id = singlePollTemplate.quizzes?.id;
      payloadData.quizzes.questions[0].id = singlePollTemplate.quizzes?.questions[0]?.id;
      payloadData.quizzes.questions[0].id = singlePollTemplate.quizzes?.questions[0]?.id;
    }

    if (!actionTimeError) {
      console.log('Payload sent:', payloadData);
      settingAction.updateTemplateRequest(payloadData);
    }
  };

  useEffect(() => {
    if (isTemplateUpdated) {
      if (ref.current) {
        ref.current.click();
      }
      handleCloseClick();
    }
  }, [isTemplateUpdated]);

  useEffect(() => {
    if (singlePollTemplate && singlePollTemplate?.pollType == 2) {
      const resetData = {
        pollTitle: singlePollTemplate?.quizzes?.name || '',
        description: singlePollTemplate?.quizzes?.questions[0]?.description || ''
      };
      setShowDescription(!!singlePollTemplate?.quizzes?.questions[0]?.description);
      setDisplayResultData(singlePollTemplate?.displayResults || false);
      setTime(singlePollTemplate?.pollDisplayTime || '00:00:00');
      reset(resetData);
    }
  }, [singlePollTemplate, reset]);

  const handleCloseClick = () => {
    setTime('00:00:00');
    setActionTimeError(false);
    setShowDescription(false);
    setDisplayResultData(false);
    setIsDataSubmitted(false);

    reset({
      pollTitle: '',
      description: '',
      displayResultsTimer: '15',
      pollDisplayTimeString: ''
    });
  };

  const handleDisplayAtTheEndClick = () => {
    setTime('00:01:00');
  };

  return (
    <div>
      <div
        className="modal fade"
        id="UpdateOpenTextModal"
        aria-labelledby="UpdateOpenTextModalLabel"
        aria-hidden="true">
        <div className="modal-dialog relative w-auto pointer-events-none max-w-xl">
          <div className="modal-content">
            <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 rounded-t-md">
              <h5
                className="text-xl font-semibold leading-normal text-center w-full"
                id="UpdateOpenTextModalLabel">
                Open Text
                <span>
                  {actionTimeError && (
                    <p className="text-red-500 text-xs error">
                      Poll Display Time cannot be greater than Template Duration
                    </p>
                  )}
                </span>
              </h5>
              <button
                ref={ref}
                onClick={handleCloseClick}
                type="button"
                className="dismiss-btn"
                data-bs-dismiss="modal"
                aria-label="Close"></button>
            </div>
            <form className="px-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body relative py-4 border-b">
                <select disabled className="input-style w-full mb-5" {...register('pollType')}>
                  <option value={0}>Quiz</option>
                  <option value={1}>Multiple Choice</option>
                  <option value={2}>Open Text</option>
                  <option value={3}>Star Rating</option>
                </select>
                <div className="flex items-center justify-center">
                  <div className="form-group mb-3 mr-3 w-full">
                    <label className="block text-sm font-medium mb-1">Template Duration</label>
                    <input
                      disabled
                      type="text"
                      className="input-style quiz-disable"
                      value="00:01:00"
                    />
                  </div>
                  <div className="form-group mb-3 w-full flex items-center">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Poll Display Time (hh:mm:ss)
                      </label>
                      <TimePicker
                        className="w-full"
                        onChange={onChange}
                        value={time ? moment(time, 'HH:mm:ss') : undefined}
                      />
                    </div>
                    <div className="ml-2 mt-3">
                      <button
                        onClick={handleDisplayAtTheEndClick}
                        type="button"
                        className="btn-primary w-auto mt-3 mr-auto ml-0 borderonly-btn whitespace-nowrap">
                        Display at end
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="form-group mb-3 w-full mr-3">
                    <label className="block text-sm font-medium mb-1">Display Result to User</label>
                    <div className="form-check form-switch pl-0 mr-3 relative">
                      <input
                        onChange={(e) => setDisplayResultData(e.target.checked)}
                        className="form-check-input switch-btn"
                        type="checkbox"
                        role="switch"
                        id="displayResults"
                        checked={displayResultData}
                      />
                      <i className="fas fa-check checkbox-tick hidden"></i>
                    </div>
                  </div>
                  <div className={`form-group mb-3 w-full ${displayResultData ? '' : 'hidden'}`}>
                    <label className="block text-sm font-medium mb-1">Display Result Timer</label>
                    <select className="input-style" {...register('displayResultsTimer')}>
                      <option value={15}>15</option>
                      <option value={30}>30</option>
                      <option value={45}>45</option>
                      <option value={60}>60</option>
                      <option value={90}>90</option>
                      <option value={120}>120</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 px-0">
                  <div className="border rounded-md">
                    <div className="flex items-center justify-between border-b px-5 py-4">
                      <p className="font-semibold mb-0">Question</p>
                    </div>
                    <div className="p-4 pt-2 questions-side-card">
                      <div className="flex-items">
                        <label className="font-semibold"></label>
                        <div className="dropdown relative">
                          <button
                            className="dropdown-toggle border-0 input-style mx-auto flex-items"
                            type="button"
                            id="action-dropdown"
                            data-bs-toggle="dropdown"
                            aria-expanded="false">
                            <i className="fas fa-chevron-down"></i>
                          </button>
                          <ul
                            className="dropdown-menu dropdown-menu-ul w-32"
                            aria-labelledby="action-dropdown">
                            <li className="cursor-pointer" onClick={() => setShowDescription(true)}>
                              <span className="action-menu">Add description</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="form-group mb-3 w-full relative">
                        <input
                          type="text"
                          className="input-style pl-0"
                          placeholder="What would you like to ask ?"
                          {...register('pollTitle')}
                        />
                      </div>
                      <div
                        className={`form-group mb-3 w-full relative ${
                          showDescription ? '' : 'hidden'
                        }`}>
                        <input
                          type="text"
                          className="input-style pl-0"
                          placeholder="Description"
                          {...register('description')}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer flex flex-shrink-0 flex-wrap items-center justify-center p-4 py-6 rounded-b-md">
                <button
                  disabled={isTemplateUpdatedLoading || isDataSubmitted}
                  type="submit"
                  className={`px-6 btn-primary w-auto whitespace-nowrap mr-2 ml-0 mt-0 `}>
                  Save Changes
                </button>
                <button
                  onClick={handleCloseClick}
                  type="button"
                  className="mx-0 px-6 btn-primary discard-btn w-auto whitespace-nowrap mt-0"
                  data-bs-dismiss="modal">
                  Close
                </button>
                <span className="ml-1">{isTemplateUpdatedLoading && <Loader />}</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateOpenTextModal;
